import React, { createContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { UserDto } from '../types';
import { notificationService } from '../services/notificationService';
import { authService } from '../services/apiClient';

const getTokenExpiry = (jwtToken: string): number | null => {
  try {
    const payload = JSON.parse(atob(jwtToken.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'user';
const AUTH_LAST_ACTIVITY_AT_KEY = 'authLastActivityAt';
const AUTH_HAS_LOGGED_IN_BEFORE_KEY = 'authHasLoggedInBefore';
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const ACTIVITY_PERSIST_THROTTLE_MS = 5000;

interface AuthContextType {
  user: UserDto | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: UserDto) => void;
  logout: () => void;
  setUser: (user: UserDto | null) => void;
  setToken: (token: string | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivityAt, setLastActivityAt] = useState<number | null>(null);
  const lastActivityAtRef = useRef<number | null>(null);

  const clearAuthState = useCallback(() => {
    setToken(null);
    setUser(null);
    setLastActivityAt(null);
    lastActivityAtRef.current = null;
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_LAST_ACTIVITY_AT_KEY);
    localStorage.removeItem('authSessionDate');
  }, []);

  const handleInactivityTimeout = useCallback((showToast: boolean) => {
    if (showToast) {
      notificationService.info('Session timed out due to inactivity. Please log in again.');
    }
    clearAuthState();
  }, [clearAuthState]);

  const recordActivity = useCallback((now: number = Date.now()) => {
    lastActivityAtRef.current = now;
    setLastActivityAt(now);
    localStorage.setItem(AUTH_LAST_ACTIVITY_AT_KEY, now.toString());
  }, []);

  // Load auth state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const storedUser = localStorage.getItem(AUTH_USER_KEY);
    const storedLastActivityAtRaw = localStorage.getItem(AUTH_LAST_ACTIVITY_AT_KEY);
    const hasLoggedInBefore = localStorage.getItem(AUTH_HAS_LOGGED_IN_BEFORE_KEY) === 'true';

    if (storedToken && storedUser) {
      try {
        const storedLastActivityAt = Number(storedLastActivityAtRaw);
        const hasValidLastActivityAt = Number.isFinite(storedLastActivityAt) && storedLastActivityAt > 0;
        const effectiveLastActivityAt = hasValidLastActivityAt ? storedLastActivityAt : Date.now();

        if ((Date.now() - effectiveLastActivityAt) >= INACTIVITY_TIMEOUT_MS) {
          handleInactivityTimeout(hasLoggedInBefore);
          setIsLoading(false);
          return;
        }

        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        lastActivityAtRef.current = effectiveLastActivityAt;
        setLastActivityAt(effectiveLastActivityAt);
        if (!hasValidLastActivityAt) {
          localStorage.setItem(AUTH_LAST_ACTIVITY_AT_KEY, effectiveLastActivityAt.toString());
        }
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('Failed to restore auth state:', error);
        clearAuthState();
      }
    } else if (storedToken || storedUser || storedLastActivityAtRaw) {
      clearAuthState();
    }

    setIsLoading(false);
  }, [clearAuthState, handleInactivityTimeout]);

  useEffect(() => {
    if (!token) {
      return;
    }

    let lastPersistedAt = 0;

    const onActivity = () => {
      const now = Date.now();
      if ((now - lastPersistedAt) < ACTIVITY_PERSIST_THROTTLE_MS) {
        return;
      }

      lastPersistedAt = now;
      recordActivity(now);
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [token, recordActivity]);

  useEffect(() => {
    if (!token || !lastActivityAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if ((Date.now() - lastActivityAt) >= INACTIVITY_TIMEOUT_MS) {
        handleInactivityTimeout(true);
      }
    }, 30 * 1000);

    return () => window.clearInterval(intervalId);
  }, [token, lastActivityAt, handleInactivityTimeout]);

  // Proactive token refresh: schedule 5 minutes before the JWT expires.
  // Only refreshes if the user has been active within the inactivity window.
  useEffect(() => {
    if (!token) return;

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    const REFRESH_BEFORE_MS = 5 * 60 * 1000;
    const msUntilRefresh = expiry - Date.now() - REFRESH_BEFORE_MS;

    if (msUntilRefresh <= 0) return;

    const timerId = window.setTimeout(async () => {
      const lastActive = lastActivityAtRef.current ?? Date.now();
      if ((Date.now() - lastActive) >= INACTIVITY_TIMEOUT_MS) {
        // User inactive – skip refresh; inactivity timer will log them out
        return;
      }

      try {
        const response = await authService.refresh();
        if (response.success && response.token) {
          setToken(response.token);
          localStorage.setItem(AUTH_TOKEN_KEY, response.token);
          if (response.user) {
            setUser(response.user);
            localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.user));
          }
        } else {
          clearAuthState();
        }
      } catch {
        // 401 responses are handled by the axios interceptor (redirect to /login)
      }
    }, msUntilRefresh);

    return () => window.clearTimeout(timerId);
  }, [token, clearAuthState]);

  const login = (newToken: string, newUser: UserDto) => {
    setToken(newToken);
    setUser(newUser);
    const loginTimestamp = Date.now();
    setLastActivityAt(loginTimestamp);
    localStorage.setItem(AUTH_TOKEN_KEY, newToken);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(newUser));
    localStorage.setItem(AUTH_LAST_ACTIVITY_AT_KEY, loginTimestamp.toString());
    localStorage.setItem(AUTH_HAS_LOGGED_IN_BEFORE_KEY, 'true');
    localStorage.removeItem('authSessionDate');
  };

  const logout = () => {
    clearAuthState();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        logout,
        setUser,
        setToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
